import React, { ChangeEvent, useState } from "react";
import {
  Avatar,
  Badge,
  Box,
  Button,
  Card,
  IconButton,
  Stack,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import UploadIcon from "@mui/icons-material/Upload";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";

// —— Netflix-ish tokens (visual only)
const COLORS = {
  bg: "#0b0b0b",
  panel: "#141414",
  text: "#ffffff",
  muted: "rgba(255,255,255,0.75)",
  border: "rgba(255,255,255,0.12)",
  red: "#E50914",
};

type FileKind = "profile" | "cover";

interface Props {
  profileUrl?: string;
  coverUrl?: string;
  onChange: (files: { profile?: File; cover?: File }) => void;
}

const MediaSettings: React.FC<Props> = ({ profileUrl, coverUrl, onChange }) => {
  const [preview, setPreview] = useState<{ profile?: string; cover?: string }>({
    profile: profileUrl,
    cover: coverUrl,
  });

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const handleFile = (kind: FileKind) => (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    onChange({ [kind]: file });
    setPreview((p) => ({ ...p, [kind]: URL.createObjectURL(file) }));
  };

  const clear = (kind: FileKind) =>
    setPreview((p) => ({ ...p, [kind]: undefined }));

  const UploadBtn = ({
    kind,
    label,
  }: {
    kind: FileKind;
    label: string;
  }) => (
    <Button
      component="label"
      variant="contained"
      startIcon={<UploadIcon />}
      sx={{
        textTransform: "none",
        fontFamily: "'Lexend', sans-serif",
        fontWeight: 500,
        color: COLORS.text,
        background: COLORS.red,
        "&:hover": {
          background: "#b20710",
        },
      }}
    >
      {label}
      <input hidden accept="image/*" type="file" onChange={handleFile(kind)} />
    </Button>
  );

  return (
    <Card
      variant="outlined"
      sx={{
        p: { xs: 2, sm: 3 },
        borderRadius: 2,
        fontFamily: "'Lexend', sans-serif",
        bgcolor: COLORS.panel,
        border: `1px solid ${COLORS.border}`,
        color: COLORS.text,
      }}
    >
      <Stack spacing={4}>
        {/* ---------- Profile Image ---------- */}
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={3}
          alignItems="center"
        >
          <Badge
            overlap="circular"
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            badgeContent={
              <Tooltip title="Change">
                <IconButton
                  size="small"
                  component="label"
                  sx={{
                    bgcolor: COLORS.panel,
                    border: `1px solid ${COLORS.border}`,
                    color: COLORS.text,
                    "&:hover": {
                      bgcolor: "rgba(255,255,255,0.1)",
                    },
                  }}
                >
                  <EditIcon fontSize="small" />
                  <input hidden type="file" accept="image/*" onChange={handleFile("profile")} />
                </IconButton>
              </Tooltip>
            }
          >
            <Avatar
              src={preview.profile || "/placeholder.jpg"}
              sx={{ width: 104, height: 104 }}
            />
          </Badge>

         <Stack
  direction="row"   // ← always row
  spacing={1}
  mt={{ xs: 2, sm: 0 }}
>
            <UploadBtn kind="profile" label="Upload" />
            {preview.profile && (
              <Tooltip title="Remove">
                <IconButton color="error" onClick={() => clear("profile")}>
                  <DeleteIcon />
                </IconButton>
              </Tooltip>
            )}
          </Stack>
        </Stack>

        {/* ---------- Cover Image ---------- */}
        <Box>
          <Typography fontWeight={600} mb={1} sx={{ color: COLORS.text }}>
            Cover Image
          </Typography>

          <Box
            sx={{
              position: "relative",
              width: "100%",
              pt: "56.25%",
              borderRadius: 2,
              overflow: "hidden",
              boxShadow: 1,
            }}
          >
            <Box
              component="img"
              src={preview.cover || "/cover-placeholder.jpg"}
              alt="cover"
              sx={{
                position: "absolute",
                inset: 0,
                width: "100%",
                height: "100%",
                objectFit: "cover",
              }}
            />
            {/* Hover overlay */}
            <Box
              sx={{
                position: "absolute",
                inset: 0,
                bgcolor: "rgba(0,0,0,0.3)",
                opacity: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                transition: "opacity .25s",
                "&:hover": { opacity: 1 },
              }}
            >
              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={1}
              >
                <UploadBtn kind="cover" label="Change" />
                {preview.cover && (
                  <Tooltip title="Remove">
                    <IconButton color="error" onClick={() => clear("cover")}>
                      <DeleteIcon />
                    </IconButton>
                  </Tooltip>
                )}
              </Stack>
            </Box>
          </Box>
        </Box>
      </Stack>
    </Card>
  );
};

export default MediaSettings;
